import React, { useState } from 'react'
import { IconButton, InputAdornment, Stack, TextField, Typography } from '@mui/material'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { yupResolver } from '@hookform/resolvers/yup/dist/yup'
import api from '../../../services/api'
import * as yup from 'yup'
import { translate } from '../../../i18n'
import { Button } from '../../../components/Button'
import Iconify from '../../../components/Iconify'
import { useTheme } from '@mui/material/styles'
import { useNavigate } from 'react-router-dom'
import { useAppDispatch } from 'redux/hooks'
import { showMessage } from 'redux/reducers/snackbar/snackbar.actions'

const forgotAddPasswordSchema = yup.object({
  password: yup
    .string()
    .trim()
    .required(() => translate('forgot.required.password')),
  passwordConfirmation: yup
    .string()
    .trim()
    .required(() => translate('forgot.required.passwordConfirmation'))
    .oneOf([yup.ref('password'), null], translate('forgot.password.newPassword'))
})

interface ForgotAddPasswordProps {
  password: string
  passwordConfirmation: string
}

export interface ForgotAddPasswordFormProps {
  id: string
  code: string
}
export default function ForgotAddPasswordForm ({ id, code }: ForgotAddPasswordFormProps) {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const theme = useTheme()
  const navigate = useNavigate()
  const [loading, setLoading] = useState<boolean>(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmation, setShowConfirmation] = useState(false)
  const {
    control,
    handleSubmit,
    formState: { errors }
  } = useForm<ForgotAddPasswordProps>({
    resolver: yupResolver(forgotAddPasswordSchema)
  })

  const handleSend = async (dataPost: ForgotAddPasswordProps) => {
    setLoading(true)
    try {
      const result = await api.post(`/users/${id}/new-password`,
        { code, password: dataPost.password },
        { headers: { 'Content-Type': 'application/json' } })
      if (result) {
        dispatch(showMessage({ message: `${t('account.password_changed_success')}` }))
        navigate('/signIn')
      }
    } catch (e) {
      dispatch(showMessage({ message: `${t('general.error')}`, type: 'error' }))
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <Stack alignItems="center" spacing={3} mb={3}>
        <Iconify icon="twemoji:confetti-ball" width={80} color={theme.palette.primary.main} />
        <Typography variant="body1" textAlign="center">{t('forgot.titleAddPassword')}</Typography>
      </Stack>
      <Stack mb={2} mt={2}>
        <Controller
          control={control}
          name="password"
          render={({ field: { onChange } }) => (
            <TextField
              placeholder={t<string>('forgot.field.password.placeholder')}
              label={t<string>('forgot.field.password.label')}
              type={showPassword ? 'text' : 'password'}
              onChange={onChange}
              autoCapitalize="none"
              helperText={errors.password?.message}
              error={errors.password?.message !== undefined}
              InputProps={{
                autoComplete: 'off',
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                      <Iconify icon={showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          )} />
      </Stack>
      <Stack sx={{ mb: '20px' }}>
        <Controller
          control={control}
          name="passwordConfirmation"
          render={({ field: { onChange } }) => (
            <TextField
              placeholder={t<string>('forgot.field.passwordConfirmation.placeholder')}
              label={t<string>('forgot.field.passwordConfirmation.label')}
              type={showConfirmation ? 'text' : 'password'}
              onChange={onChange}
              autoCapitalize="none"
              helperText={errors.passwordConfirmation?.message}
              error={errors.passwordConfirmation?.message !== undefined}
              InputProps={{
                autoComplete: 'off',
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowConfirmation(!showConfirmation)} edge="end">
                      <Iconify icon={showConfirmation ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          )} />
      </Stack>
      <Stack>
        <Button
          title={t('forgot.btn.reset')}
          isLoading={loading}
          onClick={handleSubmit(handleSend)}
        />
      </Stack>
    </>
  )
}
